import { Injectable } from '@angular/core';

export interface ZipCodeInfo {
  zipPattern: string;
  zipMask: string;
  zipPlaceholder: string;
  description?: string;
}

export interface CountryZipCodeConfig {
  [countryCode: string]: ZipCodeInfo;
}

@Injectable({
  providedIn: 'root',
})
export class ZipCodeService {
  private readonly zipCodeConfigs: CountryZipCodeConfig = {
    // North America
    USA: {
      zipPattern: '^(\\d{5}(-\\d{4})?|\\d{9})$',
      zipMask: '00000||00000-0000||000000000',
      zipPlaceholder: '12345 or 12345-6789',
      description: '5-digit ZIP or ZIP+4',
    },
    CAN: {
      zipPattern: '^\\s*[A-Za-z]\\d[A-Za-z]\\s?\\d[A-Za-z]\\d\\s*$',
      zipMask: 'A0A 0A0',
      zipPlaceholder: 'A1B 2C3',
      description: 'Canadian postal code',
    },
    MEX: {
      zipPattern: '^\\d{5}$',
      zipMask: '00000',
      zipPlaceholder: '12345',
      description: '5-digit postal code',
    },

    // Europe
    GBR: {
      zipPattern: '^[A-Za-z]{1,2}\\d{1,2}[A-Za-z]?\\s?\\d[A-Za-z]{2}$',
      zipMask: 'A0 0AA||A0A 0AA||A00 0AA||AA0 0AA||AA0A 0AA||AA00 0AA',
      zipPlaceholder: 'SW1A 1AA',
      description: 'UK postcode',
    },
    DEU: {
      zipPattern: '^\\d{5}$',
      zipMask: '00000',
      zipPlaceholder: '12345',
      description: 'German postleitzahl',
    },
    FRA: {
      zipPattern: '^\\d{5}$',
      zipMask: '00000',
      zipPlaceholder: '75001',
      description: 'French postal code',
    },
    ITA: {
      zipPattern: '^\\d{5}$',
      zipMask: '00000',
      zipPlaceholder: '00100',
      description: 'Italian CAP',
    },
    ESP: {
      zipPattern: '^\\d{5}$',
      zipMask: '00000',
      zipPlaceholder: '28001',
      description: 'Spanish postal code',
    },
    NLD: {
      zipPattern: '^\\d{4}\\s?[A-Za-z]{2}$',
      zipMask: '0000 AA',
      zipPlaceholder: '1012 AB',
      description: 'Dutch postcode',
    },
    BEL: {
      zipPattern: '^\\d{4}$',
      zipMask: '0000',
      zipPlaceholder: '1000',
      description: 'Belgian postal code',
    },
    CHE: {
      zipPattern: '^\\d{4}$',
      zipMask: '0000',
      zipPlaceholder: '8000',
      description: 'Swiss postal code',
    },
    AUT: {
      zipPattern: '^\\d{4}$',
      zipMask: '0000',
      zipPlaceholder: '1010',
      description: 'Austrian postal code',
    },
    POL: {
      zipPattern: '^\\d{2}-\\d{3}$',
      zipMask: '00-000',
      zipPlaceholder: '00-001',
      description: 'Polish postal code',
    },
    SWE: {
      zipPattern: '^\\d{3}\\s?\\d{2}$',
      zipMask: '000 00',
      zipPlaceholder: '123 45',
      description: 'Swedish postnummer',
    },
    NOR: {
      zipPattern: '^\\d{4}$',
      zipMask: '0000',
      zipPlaceholder: '0001',
      description: 'Norwegian postnummer',
    },
    DNK: {
      zipPattern: '^\\d{4}$',
      zipMask: '0000',
      zipPlaceholder: '1000',
      description: 'Danish postnummer',
    },
    FIN: {
      zipPattern: '^\\d{5}$',
      zipMask: '00000',
      zipPlaceholder: '00100',
      description: 'Finnish postinumero',
    },
    AND: {
      zipPattern: '^AD\\d{3}$',
      zipMask: 'AA000',
      zipPlaceholder: 'AD123',
      description: 'Andorran postal code',
    },
    BRB: {
      zipPattern: '^BB\\d{5}$',
      zipMask: 'AA00000',
      zipPlaceholder: 'BB12345',
      description: 'Barbados postal code',
    },
    BMU: {
      zipPattern: '^[A-Za-z]{2}\\d{2}$',
      zipMask: 'AA00',
      zipPlaceholder: 'AB12',
      description: 'Bermuda postal code',
    },
    BRN: {
      zipPattern: '^[A-Za-z]{2}\\d{4}$',
      zipMask: 'AA0000',
      zipPlaceholder: 'AB1234',
      description: 'Brunei postal code',
    },

    // Asia
    IND: {
      zipPattern: '^\\d{6}$',
      zipMask: '000000',
      zipPlaceholder: '110001',
      description: 'Indian PIN code',
    },
    CHN: {
      zipPattern: '^\\d{6}$',
      zipMask: '000000',
      zipPlaceholder: '100000',
      description: 'Chinese postal code',
    },
    JPN: {
      zipPattern: '^\\d{3}-?\\d{4}$',
      zipMask: '000-0000',
      zipPlaceholder: '100-0001',
      description: 'Japanese postal code',
    },
    KOR: {
      zipPattern: '^\\d{5}$',
      zipMask: '00000',
      zipPlaceholder: '12345',
      description: 'Korean postal code',
    },
    SGP: {
      zipPattern: '^\\d{6}$',
      zipMask: '000000',
      zipPlaceholder: '123456',
      description: 'Singapore postal code',
    },
    PHL: {
      zipPattern: '^\\d{4}$',
      zipMask: '0000',
      zipPlaceholder: '1000',
      description: 'Philippine postal code',
    },
    THA: {
      zipPattern: '^\\d{5}$',
      zipMask: '00000',
      zipPlaceholder: '10100',
      description: 'Thai postal code',
    },
    VNM: {
      zipPattern: '^\\d{6}$',
      zipMask: '000000',
      zipPlaceholder: '100000',
      description: 'Vietnamese postal code',
    },
    IDN: {
      zipPattern: '^\\d{5}$',
      zipMask: '00000',
      zipPlaceholder: '10110',
      description: 'Indonesian postal code',
    },
    MYS: {
      zipPattern: '^\\d{5}$',
      zipMask: '00000',
      zipPlaceholder: '50000',
      description: 'Malaysian postcode',
    },

    // Oceania
    AUS: {
      zipPattern: '^\\d{4}$',
      zipMask: '0000',
      zipPlaceholder: '2000',
      description: 'Australian postcode',
    },
    NZL: {
      zipPattern: '^\\d{4}$',
      zipMask: '0000',
      zipPlaceholder: '1010',
      description: 'New Zealand postcode',
    },

    // South America
    BRA: {
      zipPattern: '^\\d{5}-?\\d{3}$',
      zipMask: '00000-000',
      zipPlaceholder: '01310-100',
      description: 'Brazilian CEP',
    },
    ARG: {
      zipPattern: '^[A-Za-z]\\d{4}[A-Za-z]{3}$',
      zipMask: 'A0000AAA',
      zipPlaceholder: 'C1234ABC',
      description: 'Argentine postal code',
    },
    CHL: {
      zipPattern: '^\\d{7}$',
      zipMask: '0000000',
      zipPlaceholder: '8320000',
      description: 'Chilean postal code',
    },
    COL: {
      zipPattern: '^\\d{6}$',
      zipMask: '000000',
      zipPlaceholder: '110111',
      description: 'Colombian postal code',
    },
    PER: {
      zipPattern: '^\\d{5}$',
      zipMask: '00000',
      zipPlaceholder: '15001',
      description: 'Peruvian postal code',
    },

    // Middle East
    ARE: {
      zipPattern: '^[A-Za-z0-9]{1,10}$',
      zipMask: '',
      zipPlaceholder: 'ABC123',
      description: 'UAE postal code',
    },
    SAU: {
      zipPattern: '^\\d{5}(-\\d{4})?$',
      zipMask: '00000||00000-0000',
      zipPlaceholder: '11564',
      description: 'Saudi postal code',
    },
    ISR: {
      zipPattern: '^\\d{7}$',
      zipMask: '0000000',
      zipPlaceholder: '9100001',
      description: 'Israeli postal code',
    },
    TUR: {
      zipPattern: '^\\d{5}$',
      zipMask: '00000',
      zipPlaceholder: '34000',
      description: 'Turkish postal code',
    },

    // Africa
    ZAF: {
      zipPattern: '^\\d{4}$',
      zipMask: '0000',
      zipPlaceholder: '0001',
      description: 'South African postal code',
    },
    EGY: {
      zipPattern: '^\\d{5}$',
      zipMask: '00000',
      zipPlaceholder: '11511',
      description: 'Egyptian postal code',
    },

    // Caribbean
    PRI: {
      zipPattern: '^\\d{5}(-\\d{4})?$',
      zipMask: '00000||00000-0000',
      zipPlaceholder: '00901',
      description: 'Puerto Rico ZIP',
    },
  };

  /**
   * Cache of compiled regular expressions keyed by pattern string.
   *
   * Note: Different country codes that use the same pattern will share
   * the same RegExp instance. This is intentional for performance and
   * is safe because the RegExp instances are treated as immutable and
   * are not created with stateful flags such as the "g" (global) flag.
   */
  private readonly regexCache = new Map<string, RegExp>();

  // Default config for countries without specific patterns
  private readonly defaultZipCodeInfo: ZipCodeInfo = {
    zipPattern: '^[A-Za-z0-9\\s-]{1,10}$',
    zipMask: '',
    zipPlaceholder: 'Enter postal code',
    description: 'Alphanumeric postal code',
  };

  /**
   * Get zip code information for a specific country
   * @param countryCode - Country code (codeA3 format like 'USA', 'CAN', etc.)
   * @returns ZipCodeInfo object with pattern, mask, and placeholder
   */
  getZipCodeInfo(countryCode: string): ZipCodeInfo {
    if (!countryCode) {
      return this.defaultZipCodeInfo;
    }

    // Try exact match first
    if (this.zipCodeConfigs[countryCode]) {
      return this.zipCodeConfigs[countryCode];
    }

    // Try uppercase
    const upperCode = countryCode.toUpperCase();
    if (this.zipCodeConfigs[upperCode]) {
      return this.zipCodeConfigs[upperCode];
    }

    // Return default
    return this.defaultZipCodeInfo;
  }

  /**
   * Get the regex pattern for zip code validation
   * @param countryCode - Country code
   * @returns Regex pattern string
   */
  getZipCodePattern(countryCode: string): string {
    return this.getZipCodeInfo(countryCode).zipPattern;
  }

  /**
   * Get the input mask for formatting
   * @param countryCode - Country code
   * @returns Mask string for ngx-mask
   */
  getZipCodeMask(countryCode: string): string {
    return this.getZipCodeInfo(countryCode).zipMask;
  }

  /**
   * Get the placeholder text
   * @param countryCode - Country code
   * @returns Placeholder string
   */
  getZipCodePlaceholder(countryCode: string): string {
    return this.getZipCodeInfo(countryCode).zipPlaceholder;
  }

  /**
   * Get the description of the zip code format
   * @param countryCode - Country code
   * @returns Description string
   */
  getZipCodeDescription(countryCode: string): string {
    return this.getZipCodeInfo(countryCode).description || 'Postal code';
  }

  /**
   * Validate a zip code against a country's pattern
   * @param zipCode - The zip code to validate
   * @param countryCode - Country code
   * @returns true if valid, false otherwise
   */
  validateZipCode(zipCode: string, countryCode: string): boolean {
    if (!zipCode || !countryCode) {
      return false;
    }

    return this.testZipCode(zipCode, countryCode);
  }

  /**
   * Test a zip code against a country's compiled regex (cached)
   * @param zipCode - The zip code to test
   * @param countryCode - Country code
   * @returns true if valid, false otherwise
   */
  testZipCode(zipCode: string, countryCode: string): boolean {
    const pattern = this.getZipCodePattern(countryCode);
    let regex = this.regexCache.get(pattern);
    if (!regex) {
      regex = new RegExp(pattern);
      this.regexCache.set(pattern, regex);
    }
    return regex.test(zipCode);
  }

  /**
   * Get all supported country codes
   * @returns Array of country codes with zip code patterns
   */
  getSupportedCountries(): string[] {
    return Object.keys(this.zipCodeConfigs);
  }

  /**
   * Check if a country has a specific zip code pattern
   * @param countryCode - Country code
   * @returns true if country has specific pattern, false if using default
   */
  hasSpecificPattern(countryCode: string): boolean {
    return !!this.zipCodeConfigs[countryCode] || !!this.zipCodeConfigs[countryCode.toUpperCase()];
  }
}
